import {
  Box,
  Toolbar as MuiToolbar,
  ButtonBase,
  Menu,
  MenuItem,
  Tab,
  Tabs,
} from "@mui/material";
import { Theme } from "@mui/material/styles";
import { Link } from "@platform/navigation-control";
import * as React from "react";
import kebabCase from "lodash/kebabCase";

import { Icon } from "../Icon";
import logoImage from "../../assets/logo.png";

import { useOverflowTabs } from "./use-overflow-tabs";

interface TabItem {
  label: string;
  path: string;
  /** Always collapse this tab into the "More" menu, regardless of available width. */
  overflow?: boolean;
}

interface Props {
  extras?: React.ReactNode;
  tabs: TabItem[];
  currentTab?: string | false;
  logo?: string;
}

/**
 * Rough width of the "More" trigger (icon + label + padding), reserved from
 * the tab-fitting budget whenever the trigger is guaranteed to render, so
 * tabs don't claim space the trigger needs once it appears.
 */
const OVERFLOW_TRIGGER_WIDTH_PX = 88;

const PrimaryBarOverflowMenu = ({
  tabs,
  active,
  currentTab,
  anchorEl,
  onOpen,
  onClose,
}: {
  tabs: TabItem[];
  active: boolean;
  currentTab?: string | false;
  anchorEl: HTMLElement | null;
  onOpen: (target: HTMLElement) => void;
  onClose: () => void;
}): React.ReactElement => (
  <>
    {/* className is a hook for the shared theme's rules, same as .app-bar-tabs
        on the sibling Tabs — lets a page (e.g. the dashboard) recolor this
        trigger the same way it recolors the other tabs, without PrimaryBar
        needing to know about page-specific state. */}
    <ButtonBase
      data-testid="ds-primary-bar-overflow-trigger"
      className="app-bar-tabs-overflow-trigger"
      aria-haspopup="menu"
      aria-expanded={Boolean(anchorEl)}
      aria-label="More navigation items"
      onClick={(event: React.MouseEvent<HTMLElement>) =>
        onOpen(event.currentTarget)
      }
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        px: 2,
        flex: "none",
        typography: "button",
        color: active ? "brandSupporting.main" : "text.secondary",
        borderBottom: 2,
        borderColor: active ? "brandSupporting.main" : "transparent",
      }}
    >
      More
      <Icon name="expand" />
    </ButtonBase>
    <Menu
      data-testid="ds-primary-bar-overflow-menu"
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
    >
      {tabs.map((item) => (
        <MenuItem
          data-testid={`ds-primary-bar-overflow-item-${kebabCase(item.label)}`}
          key={item.path}
          component={Link}
          to={item.path}
          selected={item.path === currentTab}
          onClick={onClose}
        >
          {item.label}
        </MenuItem>
      ))}
    </Menu>
  </>
);

const PrimaryBar = React.forwardRef<HTMLDivElement, Props>(
  ({ logo = logoImage, currentTab, tabs, extras }, ref) => {
    const priorityTabs = tabs.filter((tab) => !tab.overflow);
    const pinnedOverflowTabs = tabs.filter((tab) => tab.overflow);
    const triggerIsAlwaysShown = pinnedOverflowTabs.length > 0;

    const [overflowAnchorEl, setOverflowAnchorEl] =
      React.useState<HTMLElement | null>(null);

    const { containerRef, measureRef, visibleCount } = useOverflowTabs(
      priorityTabs.length,
      OVERFLOW_TRIGGER_WIDTH_PX,
      triggerIsAlwaysShown,
      // The "More" menu locks background scroll, which can transiently
      // resize this bar's fixed-position ancestor (scrollbar hidden, or
      // MUI's own compensating padding applied) — freeze the fit while
      // it's open so that resize doesn't shuffle which tabs are visible.
      Boolean(overflowAnchorEl),
    );
    const visibleTabs = priorityTabs.slice(0, visibleCount);
    const collapsedTabs = priorityTabs.slice(visibleCount);
    const overflowTabs = [...collapsedTabs, ...pinnedOverflowTabs];

    const currentTabIsVisible = visibleTabs.some(
      (tab) => tab.path === currentTab,
    );

    return (
      <MuiToolbar
        data-testid="ds-primary-bar"
        sx={{
          height: "4rem",
          // Last resort for a viewport too narrow for the logo, the tabs
          // area's floor (see the tabs-container's minWidth below), and the
          // extras to all fit: scroll the bar instead of clipping content.
          overflowX: "auto",
          overflowY: "hidden",
        }}
        ref={ref}
      >
        <Link to="/" data-testid="ds-primary-bar-logo">
          <Box
            component="img"
            src={logo as string}
            marginRight={(theme: Theme) => theme.spacing(8)}
            alt="centro logo"
            width="10.25rem"
          />
        </Link>
        <Box
          data-testid="ds-primary-bar-tabs-container"
          ref={containerRef}
          flex={1}
          // Never squeeze past the "More" trigger's own width — without this
          // floor, a viewport too narrow for the logo + extras can shrink
          // this box to 0 and clip the trigger along with everything else,
          // leaving no way to reach any nav item at all.
          minWidth={overflowTabs.length > 0 ? OVERFLOW_TRIGGER_WIDTH_PX : 0}
          overflow="hidden"
          display="flex"
          alignItems="center"
        >
          <Tabs
            data-testid="ds-primary-bar-tabs"
            role="navigation"
            className="app-bar-tabs"
            aria-label="primary navigation"
            variant="standard"
            value={currentTabIsVisible ? currentTab : false}
          >
            {visibleTabs.map((item) => (
              <Tab
                data-testid={`ds-primary-bar-tab-${kebabCase(item.label)}`}
                key={item.path}
                value={item.path}
                label={item.label}
                to={item.path}
                component={Link}
              />
            ))}
          </Tabs>
          {overflowTabs.length > 0 && (
            <PrimaryBarOverflowMenu
              tabs={overflowTabs}
              active={
                !currentTabIsVisible &&
                overflowTabs.some((tab) => tab.path === currentTab)
              }
              currentTab={currentTab}
              anchorEl={overflowAnchorEl}
              onOpen={setOverflowAnchorEl}
              onClose={() => setOverflowAnchorEl(null)}
            />
          )}
        </Box>
        <Box
          ref={measureRef}
          aria-hidden
          sx={{
            position: "absolute",
            visibility: "hidden",
            height: 0,
            overflow: "hidden",
            pointerEvents: "none",
          }}
        >
          <Tabs className="app-bar-tabs" variant="standard" value={false}>
            {priorityTabs.map((item) => (
              <Tab key={item.path} label={item.label} />
            ))}
          </Tabs>
        </Box>
        {extras && (
          <Box data-testid="ds-primary-bar-extras" display="flex">
            {extras}
          </Box>
        )}
      </MuiToolbar>
    );
  },
);

PrimaryBar.displayName = "PrimaryBar";

export { PrimaryBar };
export type { TabItem };
